import { useTranslation } from 'react-i18next'
import Popup from 'components/Popup/Popup'
import Skeleton from 'components/Skeleton/Skeleton'
import { useSizeName } from 'hooks/tiles'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import type { CartUpsellId } from '../../cartUpsells.schemas'
import type { CartUpsellAnalyticsProps } from '../../cartUpsells.types'
import { useUpsellWallPrices } from '../../hooks/useUpsellWallPrices'
import type { UpsellWall } from '../../upsellWall'
import { UpsellWallPreview } from './UpsellWallPreview'
import * as S from './UpsellWallSheet.styles'

type UpsellWallSheetProps = {
  upsellId: CartUpsellId
  analyticsProps: CartUpsellAnalyticsProps
  title: string
  wall: UpsellWall
  excludedTileIndexes: ReadonlySet<number>
  onTileToggle: (tileIndex: number) => void
  footer: React.ReactNode
  isOpen: boolean
  onClose: () => void
  onExited: () => void
}

export function UpsellWallSheet({
  upsellId,
  analyticsProps,
  title,
  wall,
  excludedTileIndexes,
  onTileToggle,
  footer,
  isOpen,
  onClose,
  onExited,
}: UpsellWallSheetProps) {
  const { t } = useTranslation()
  const { getSizeName } = useSizeName()
  const includedTilesCount = wall.photos.length - excludedTileIndexes.size
  const wallPricesQuery = useUpsellWallPrices({
    tileStyle: wall.tileStyle,
    offeredTilesCount: wall.photos.length,
  })
  const includedTilesPrice = wallPricesQuery.data?.[includedTilesCount]
  const onDismiss = () => {
    analytics.track(EVENT_NAMES.CART_UPSELL_FLOW_DISMISSED, analyticsProps)
    onClose()
  }
  return (
    <Popup
      open={isOpen}
      onClose={onDismiss}
      onExited={onExited}
      maxHeight={S.MOBILE_SHEET_HEIGHT}
      fullHeight
      dialogStyles={S.POPUP_DIALOG_STYLES}
      slotProps={{
        backdrop: { 'data-testid': 'upsell-wall-sheet-backdrop' },
      }}
    >
      <S.Content data-testid="upsell-wall-sheet" data-upsell-id={upsellId}>
        <S.Body>
          <S.Header>
            <S.CloseButton
              type="button"
              onClick={onDismiss}
              data-testid="upsell-wall-sheet-close"
            >
              <S.CloseIcon />
            </S.CloseButton>
            <S.Title>{title}</S.Title>
            <S.Subtitle>
              <span>
                {includedTilesCount === 0
                  ? t('cart_upsells.select_tiles_to_continue')
                  : t(
                      includedTilesCount === 1
                        ? 'cart_upsells.wall_subtitle_one'
                        : 'cart_upsells.wall_subtitle_other',
                      {
                        count: includedTilesCount,
                        size: getSizeName(wall.tileStyle.tileSize),
                      }
                    )}
              </span>
              {includedTilesCount > 0 &&
                (wallPricesQuery.isPending ? (
                  <S.PriceGroup>
                    <span>·</span>
                    <Skeleton width={60} role="status" />
                  </S.PriceGroup>
                ) : (
                  includedTilesPrice && (
                    <S.PriceGroup>
                      <span>·</span>
                      <span data-testid="upsell-wall-price">
                        {includedTilesPrice.current}
                      </span>
                      {includedTilesPrice.original && (
                        <S.OriginalPrice data-testid="upsell-wall-original-price">
                          {includedTilesPrice.original}
                        </S.OriginalPrice>
                      )}
                    </S.PriceGroup>
                  )
                ))}
            </S.Subtitle>
          </S.Header>
          <UpsellWallPreview
            wall={wall}
            excludedTileIndexes={excludedTileIndexes}
            onTileToggle={onTileToggle}
          />
        </S.Body>
        <S.Footer>{footer}</S.Footer>
      </S.Content>
    </Popup>
  )
}
